'use client'

import { useEffect, useRef } from 'react'
import { PRODUCT_TRANSPARENT } from '@/lib/media'
import styles from './page.module.css'

/**
 * Monument product plate for the Edition 001 dossier. The transparent AVIF/WebP/
 * PNG is the always-present, server-safe baseline — it is the complete object on
 * no-JS, reduced-motion, and Save-Data paths. On capable devices this layers a
 * subtle pointer + scroll refraction/parallax by writing CSS custom properties
 * the module consumes; it never swaps or hides the object itself.
 *
 * Motion is gated exactly like the reveal stage (RevealStage): prefers-reduced-
 * motion and navigator.connection.saveData both fall back to the still plate.
 */
export default function EditionPlate() {
  const plateRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = plateRef.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    if (connection?.saveData === true) return

    let frame = 0
    const clamp = (value: number) => Math.max(-1, Math.min(1, value))

    const onPointerMove = (event: PointerEvent) => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return
        el.style.setProperty('--px', clamp((event.clientX - rect.left) / rect.width - 0.5).toFixed(3))
        el.style.setProperty('--py', clamp((event.clientY - rect.top) / rect.height - 0.5).toFixed(3))
      })
    }

    const onPointerLeave = () => {
      el.style.setProperty('--px', '0')
      el.style.setProperty('--py', '0')
    }

    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect()
        const centre = rect.top + rect.height / 2 - window.innerHeight / 2
        el.style.setProperty('--scroll', clamp(centre / window.innerHeight).toFixed(3))
      })
    }

    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerleave', onPointerLeave)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    return () => {
      cancelAnimationFrame(frame)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerleave', onPointerLeave)
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  return (
    <div ref={plateRef} className={styles.plate}>
      <span className={styles.plateAura} aria-hidden="true" />
      <picture>
        <source type="image/avif" srcSet={PRODUCT_TRANSPARENT.avif} />
        <source type="image/webp" srcSet={PRODUCT_TRANSPARENT.webp} />
        <img
          src={PRODUCT_TRANSPARENT.src}
          width={PRODUCT_TRANSPARENT.width}
          height={PRODUCT_TRANSPARENT.height}
          fetchPriority="high"
          alt="MIST by BDQ Edition 001 Peridot bottle: clear crystal with an olive-gold liquid, faceted peridot cap, and gold collar."
          className={styles.plateProduct}
        />
      </picture>
      <span className={styles.plateRefraction} aria-hidden="true" />
      <p className={styles.plateCaption}>Clear heavy crystal · faceted peridot cap · gold collar · 100&nbsp;mL</p>
    </div>
  )
}
